import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiPackage, FiRefreshCcw, FiDownloadCloud, FiX } from "react-icons/fi";
import { ReportEmbedContext } from '../reportEmbedContext';
import { PURCHASE_RETURN_REPORT, BASE_URL, GET_SUPPLIER_MASTER } from '../../utlilities/config';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Enhanced Spinner Component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
  const today = new Date();
  const todayFormatted = formatLocalDate(today);

  return filters.reduce((accumulator, filter) => {
    if (filter.type === 'date') {
      const dateValue = todayFormatted;
      return { ...accumulator, [filter.id]: dateValue };
    }

    if (filter.type === 'select') {
      const defaultValue = filter.options?.[0]?.value ?? '';
      return { ...accumulator, [filter.id]: defaultValue };
    }

    return { ...accumulator, [filter.id]: '' };
  }, {});
};

// Custom Select Styles
const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    fontSize: '14px',
    borderRadius: '0.5rem',
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
    color: '#000000',
    '&:hover': {
      border: '1px solid #cbd5e1',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 12px',
    color: '#000000',
  }),
  input: (base) => ({
    ...base,
    color: '#000000',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#000000',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9ca3af',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '40px',
  }),
  menu: (base) => ({
    ...base,
    fontSize: '14px',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : 'white',
    color: state.isSelected ? '#000000' : '#000000',
    cursor: 'pointer',
    ':active': {
      backgroundColor: '#4f46e5',
      color: '#000000',
    },
  }),
};

// Report configuration
const reportConfig = {
  filters: [
    { id: 'fromDate', type: 'date', label: 'From Date' },
    { id: 'toDate', type: 'date', label: 'To Date' },
  ],
};

// Helper function to convert number to words (Indian Format)
const numToWords = (n) => {
  if (n === 0) return "Zero Rupees Only";

  const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convert = (num) => {
    if (num < 20) return units[num];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? " " + units[num % 10] : "");
    if (num < 1000) return units[Math.floor(num / 100)] + " Hundred" + (num % 100 ? " " + convert(num % 100) : "");
    if (num < 100000) return convert(Math.floor(num / 1000)) + " Thousand" + (num % 1000 ? " " + convert(num % 1000) : "");
    if (num < 10000000) return convert(Math.floor(num / 100000)) + " Lakh" + (num % 100000 ? " " + convert(num % 100000) : "");
    return convert(Math.floor(num / 10000000)) + " Crore" + (num % 10000000 ? " " + convert(num % 10000000) : "");
  };

  const numStr = parseFloat(n).toFixed(2);
  const parts = numStr.split('.');
  const rupees = parseInt(parts[0]);
  const paise = parseInt(parts[1] || 0);

  let words = '';
  if (rupees === 0) {
    words = 'Zero';
  } else {
    words = convert(rupees);
  }

  words = words.trim() + ' Rupees';
  if (paise > 0) {
    words += ' and ' + convert(paise).trim() + ' Paise';
  }

  return words + ' Only';
};

// Format date to DD/MM/YYYY
const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
};

export const PurchaseReturnReport = () => {
  const embedded = useContext(ReportEmbedContext);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
  const [reportData, setReportData] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [suppliersMaster, setSuppliersMaster] = useState([]);
  const [generalsettings, setGeneralSettings] = useState({});
  const token = localStorage.getItem('accessToken');
  const supplierSearchDebounceRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(500);

  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;
  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  useEffect(() => {
    fetchGeneralSettings();
    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'FilterText': '',
          },
        });

        if (response.status === 200) {
          setSuppliersMaster(response.data?.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
      }
    };

    if (token) {
      fetchSuppliers();
    }
  }, [token]);

  const fetchGeneralSettings = async () => {
    try {
      let generalSetting;
      if (isElectron()) {
        generalSetting = await ipcRenderer.invoke('store-get', 'generalopsettings');
      }
      setGeneralSettings(generalSetting || {});
    } catch (error) {
      console.error('Error fetching general settings:', error);
    }
  };

  // Load suppliers from API based on search text (for AsyncSelect)
  const loadSupplierOptions = (inputValue, callback) => {
    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current);
    }

    if (!inputValue || inputValue.length < 2) {
      const options = (suppliersMaster || []).map((supplier) => ({
        value: supplier.SupplierID ?? supplier.supplierID ?? supplier.Id ?? supplier.id ?? supplier.SupplierId ?? null,
        label: supplier.SupplierName ?? supplier.Name ?? '',
        ...supplier,
      }));
      const filteredOptions = inputValue
        ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
        : options;
      callback(filteredOptions);
      return;
    }

    supplierSearchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'FilterText': inputValue,
          },
        });

        if (response.status === 200) {
          const suppliers = response.data?.data || [];

          const options = suppliers.map((supplier) => ({
            value: supplier.SupplierID ?? supplier.supplierID ?? supplier.Id ?? supplier.id ?? supplier.SupplierId ?? null,
            label: supplier.SupplierName ?? supplier.Name ?? '',
            ...supplier,
          }));

          const filteredOptions = inputValue
            ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
            : options;
          callback(filteredOptions);
        } else {
          callback([]);
        }
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
        callback([]);
      }
    }, 300);
  };

  const getSelectedSupplierOption = (supplierId) => {
    if (!supplierId) return null;

    if (selectedSupplier) {
      const selectedValue = selectedSupplier.SupplierID ?? selectedSupplier.supplierID ?? selectedSupplier.Id ?? selectedSupplier.id ?? selectedSupplier.SupplierId ?? selectedSupplier.value;
      if (selectedValue === supplierId || String(selectedValue) === String(supplierId)) {
        return {
          value: selectedValue,
          label: selectedSupplier.SupplierName ?? selectedSupplier.Name ?? selectedSupplier.label ?? '',
        };
      }
    }

    const supplier = suppliersMaster.find(s => {
      const sId = s.SupplierID ?? s.supplierID ?? s.Id ?? s.id ?? s.SupplierId;
      return sId === supplierId || String(sId) === String(supplierId);
    });

    if (supplier) {
      return {
        value: supplier.SupplierID ?? supplier.supplierID ?? supplier.Id ?? supplier.id ?? supplier.SupplierId ?? null,
        label: supplier.SupplierName ?? supplier.Name ?? '',
      };
    }

    return null;
  };

  useEffect(() => {
    return () => {
      if (supplierSearchDebounceRef.current) {
        clearTimeout(supplierSearchDebounceRef.current);
      }
    };
  }, []);

  const handleSupplierChange = (selectedOption) => {
    if (!selectedOption) {
      setSelectedSupplier(null);
      return;
    }

    const supplier = selectedOption;

    if (supplier) {
      const supplierId = supplier.SupplierID ?? supplier.supplierID ?? supplier.Id ?? supplier.id ?? supplier.SupplierId ?? supplier.value;
      const existingSupplier = suppliersMaster.find(s => {
        const sId = s.SupplierID ?? s.supplierID ?? s.Id ?? s.id ?? s.SupplierId;
        return sId === supplierId || String(sId) === String(supplierId);
      });

      if (!existingSupplier) {
        setSuppliersMaster(prev => [...prev, supplier]);
      }
    }

    setSelectedSupplier(selectedOption);
  };

  const handleFilterChange = (filterId, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterId]: value,
    }));
  };

  const handleRunReport = () => {
    getPurchaseReturnReport();
  };

  // Filter data by supplier
  const filteredData = useMemo(() => {
    if (!selectedSupplier) return reportData;
    const supplierId = selectedSupplier.SupplierID ?? selectedSupplier.supplierID ?? selectedSupplier.Id ?? selectedSupplier.id ?? selectedSupplier.SupplierId ?? selectedSupplier.value;
    return reportData.filter(invoice => {
      // Match by supplier name (case insensitive)
      return invoice.supplierName?.toLowerCase() === selectedSupplier.SupplierName?.toLowerCase() ||
        invoice.supplierName?.toLowerCase() === selectedSupplier.Name?.toLowerCase() ||
        invoice.supplierName?.toLowerCase() === selectedSupplier.label?.toLowerCase();
    });
  }, [reportData, selectedSupplier]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.fromDate, filters.toDate, selectedSupplier, filteredData.length]);

  const getPurchaseReturnReport = useCallback(async () => {
    setLoading(true);
    try {
      const formatDate = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
      const toDate = filters.toDate ? new Date(filters.toDate) : null;

      const response = await axios.get(`${BASE_URL}${PURCHASE_RETURN_REPORT}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          'fromDate': formatDate(fromDate),
          'toDate': formatDate(toDate),
        },
      });

      if (response.status === 200 && response.data?.isSucess) {
        const data = response.data.data || [];
        // Sort by invoiceNo in descending order
        const sortedData = data.sort((a, b) => {
          const invoiceA = parseInt(a.invoiceNo) || 0;
          const invoiceB = parseInt(b.invoiceNo) || 0;
          return invoiceB - invoiceA;
        });
        setReportData(sortedData);
      } else {
        toast.error('Failed to fetch purchase return report', { autoClose: 1000 });
      }
    } catch (error) {
      console.error('Failed to fetch purchase return report:', error);
      toast.error('Failed to fetch purchase return report', { autoClose: 1000 });
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  const generatePDF = (invoice) => {
    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });

      // Calculate totals from items
      let totalBase = 0;
      let totalTax = 0;
      let totalCGST = 0;
      let totalSGST = 0;

      invoice.items.forEach((item) => {
        const baseAmount = parseFloat(item.price || 0) * parseFloat(item.quantity || 0);
        const taxAmount = parseFloat(item.taxAmount || 0);
        totalBase += baseAmount;
        totalTax += taxAmount;
        totalCGST += taxAmount / 2;
        totalSGST += taxAmount / 2;
      });

      const grandTotal = totalBase + totalTax;

      // Map values for header
      const firmName = generalsettings?.phShowFirmname
        ? `${generalsettings?.FirstName || ''} ${generalsettings?.LastName || ''}`.trim().toUpperCase()
        : '';
      const address = [
        generalsettings?.AddressLine1,
        generalsettings?.AddressLine2,
      ].filter(Boolean).join(', ');
      const phoneNo = [generalsettings?.MobileNo1, generalsettings?.MobileNo2].filter(Boolean).join(', ');
      const gstNo = generalsettings?.GstNo || '';

      // Supplier details
      const supplierName = invoice.supplierName || '';

      // Document details
      const debitNoteNo = invoice.invoiceNo || '';
      const debitNoteDate = formatDateDDMMYYYY(invoice.invoiceDate || new Date());

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;

      // Define table columns
      const tableColumn = [
        "No",
        "Particulars",
        "Batch",
        "Qty",
        "Rate",
        "Tax %",
        "Tax Amount",
        "CGST",
        "SGST",
        "Amount"
      ];

      const tableRows = [];

      invoice.items.forEach((item, index) => {
        const baseAmount = parseFloat(item.price || 0) * parseFloat(item.quantity || 0);
        const taxAmount = parseFloat(item.taxAmount || 0);
        const cgst = taxAmount / 2;
        const sgst = taxAmount / 2;
        const total = baseAmount + taxAmount;
        const taxCode = parseFloat(item.taxCode || 0);

        tableRows.push([
          index + 1,
          item.itemName || '',
          item.batchCode || '',
          parseFloat(item.quantity || 0).toFixed(0),
          parseFloat(item.price || 0).toFixed(2),
          `${taxCode}%`,
          parseFloat(taxAmount).toFixed(2),
          parseFloat(cgst).toFixed(2),
          parseFloat(sgst).toFixed(2),
          parseFloat(total).toFixed(2)
        ]);
      });

      const amountInWords = numToWords(grandTotal);
      const footerHeight = 110;

      // Draw header
      let y = 30;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('DEBIT NOTE', pageWidth / 2, y, { align: 'center' });
      y += 20;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(firmName, pageWidth / 2, y, { align: 'center' });
      y += 15;

      if (address) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const addressLines = doc.splitTextToSize(address, pageWidth - 300);
        addressLines.forEach(line => {
          doc.text(line, pageWidth / 2, y, { align: 'center' });
          y += 10;
        });
      }

      if (phoneNo) {
        y += 2;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(phoneNo, pageWidth / 2, y, { align: 'center' });
        y += 10;
      }

      if (gstNo) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(`GSTIN: ${gstNo}`, pageWidth / 2, y, { align: 'center' });
        y += 15;
      }

      const headerCenterBottom = y;

      // Supplier details section (left side)
      let leftY = headerCenterBottom + 10;
      const leftStartX = margin;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('To : ' + supplierName, leftStartX, leftY);
      leftY += 12;

      // Document details (right side)
      const rightStartX = pageWidth - margin;
      let rightY = headerCenterBottom + 10;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(`No: ${debitNoteNo}`, rightStartX, rightY, { align: "right" });
      rightY += 12;
      doc.text(`Date: ${debitNoteDate}`, rightStartX, rightY, { align: "right" });
      rightY += 12;

      const tableStartY = Math.max(leftY, rightY) + 5;
      const availableWidth = pageWidth - (margin * 2);

      const columnWidthRatios = [0.05, 0.24, 0.10, 0.08, 0.10, 0.08, 0.10, 0.08, 0.08, 0.09];
      const columnAlignments = ['center', 'left', 'left', 'center', 'right', 'center', 'right', 'right', 'right', 'right'];
      const columnStyles = columnWidthRatios.reduce((styles, ratio, index) => {
        styles[index] = {
          halign: columnAlignments[index],
          cellWidth: availableWidth * ratio,
        };
        return styles;
      }, {});
      const amountColumnLeft =
        margin + availableWidth * columnWidthRatios.slice(0, 9).reduce((sum, ratio) => sum + ratio, 0);
      const tableRightEdge = pageWidth - margin;

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: tableStartY,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 3,
          textColor: [0, 0, 0],
          font: 'helvetica',
          lineColor: [0, 0, 0],
          lineWidth: 0.5,
          overflow: 'linebreak',
        },
        headStyles: {
          fontStyle: 'bold',
          fillColor: [220, 220, 220],
          textColor: [0, 0, 0],
          halign: 'center',
          valign: 'middle',
          cellPadding: 4,
          lineColor: [0, 0, 0],
          lineWidth: 0.5
        },
        bodyStyles: {
          cellPadding: 3,
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
          valign: 'middle'
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255]
        },
        columnStyles,
        margin: { top: 20, bottom: footerHeight, left: margin, right: margin },
        tableWidth: availableWidth,
      });

      const finalY = doc.lastAutoTable.finalY + 10;
      const footerStartY = pageHeight - 110;

      doc.setLineWidth(0.5);
      doc.line(margin, footerStartY, pageWidth - margin, footerStartY);

      let footerY = footerStartY + 15;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Amount in words:', margin, footerY);

      footerY += 12;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const wordsLines = doc.splitTextToSize(amountInWords, pageWidth / 2);
      doc.text(wordsLines, margin, footerY);

      const signY = footerStartY + 85;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('For ' + firmName, margin, signY);

      const totalsLabelX = amountColumnLeft - 10;
      const totalsValueX = tableRightEdge;

      let totalsY = footerStartY + 15;

      doc.setFont('helvetica', 'bold');
      doc.text('Total Amount:', totalsLabelX, totalsY, { align: 'right' });
      doc.text(parseFloat(totalBase).toFixed(2), totalsValueX, totalsY, { align: 'right' });

      totalsY += 15;

      doc.text('Total Tax:', totalsLabelX, totalsY, { align: 'right' });
      doc.text(parseFloat(totalTax).toFixed(2), totalsValueX, totalsY, { align: 'right' });

      totalsY += 10;

      doc.setLineWidth(0.5);
      doc.line(totalsLabelX - 70, totalsY, totalsValueX, totalsY);

      totalsY += 12;

      doc.setFontSize(11);
      doc.text('Grand Total:', totalsLabelX, totalsY, { align: 'right' });
      doc.text(parseFloat(grandTotal).toFixed(2), totalsValueX, totalsY, { align: 'right' });

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Authorised Signatory', totalsValueX, signY, { align: 'right' });

      doc.save(`DebitNote_${debitNoteNo}_${debitNoteDate.replace(/\//g, '-')}.pdf`);

      toast.success('PDF generated successfully!', { autoClose: 1000 });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF.', { autoClose: 2000 });
    }
  };

  const formatDateString = (value) => {
    if (!value) return '-';
    try {
      const date = new Date(value);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return value;
    }
  };

  return (
    <div className="flex h-full bg-background">
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {loading && <Spinner />}

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {!embedded && (
          <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
            <div>
              <h1 className="text-xl font-semibold text-slate-800">Purchase Return Report</h1>
              <p className="mt-0.5 text-sm text-slate-500">View and download purchase return invoices</p>
            </div>
          </header>
        )}

        <main className="flex-1 overflow-hidden flex flex-col">
          <div className="flex flex-col gap-3 flex-1 min-h-0">
            {/* Filters Section */}
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex-shrink-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {reportConfig.filters.map((filter) => (
                  <div key={filter.id} className="flex flex-col">
                    <label className="text-xs font-semibold text-slate-600 uppercase mb-1">
                      {filter.label}
                    </label>
                    {filter.type === 'date' && (
                      <input
                        type="date"
                        value={filters[filter.id] || ''}
                        onChange={(e) => handleFilterChange(filter.id, e.target.value)}
                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    )}
                  </div>
                ))}

                <div className="flex flex-col">
                  <label className="text-xs font-semibold text-slate-600 uppercase mb-1">
                    Supplier (Optional)
                  </label>
                  <AsyncSelect
                    loadOptions={loadSupplierOptions}
                    placeholder="Select supplier..."
                    isClearable
                    isSearchable
                    value={getSelectedSupplierOption(selectedSupplier?.SupplierID ?? selectedSupplier?.supplierID ?? selectedSupplier?.Id ?? selectedSupplier?.id ?? selectedSupplier?.SupplierId ?? selectedSupplier?.value)}
                    onChange={handleSupplierChange}
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    defaultOptions={true}
                    cacheOptions={true}
                  />
                </div>

                <div className="flex items-end gap-2">
                  <button
                    type="button"
                    onClick={handleRunReport}
                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    <FiRefreshCcw size={16} />
                    Run Report
                  </button>
                </div>
              </div>
            </section>

            {/* Table Section */}
            <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-2">
                <div>
                  <p className="text-sm text-slate-500">
                    Showing <span className="font-semibold text-slate-700">{filteredData.length}</span> invoices
                    {selectedSupplier && ` for ${selectedSupplier.label || selectedSupplier.SupplierName || selectedSupplier.Name}`}
                  </p>
                </div>
              </div>

              <div className="flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700 border-b border-slate-200">Invoice No</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700 border-b border-slate-200">Date</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700 border-b border-slate-200">Supplier</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700 border-b border-slate-200">Net Amount</th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-700 border-b border-slate-200">Items</th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-700 border-b border-slate-200">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((invoice, index) => (
                        <tr key={invoice.invoiceNo} className={`border-b border-slate-100 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-indigo-50 transition-colors`}>
                          <td className="px-4 py-3 text-slate-800 font-medium">{invoice.invoiceNo}</td>
                          <td className="px-4 py-3 text-slate-600">{formatDateString(invoice.invoiceDate)}</td>
                          <td className="px-4 py-3 text-slate-700">{invoice.supplierName}</td>
                          <td className="px-4 py-3 text-slate-800 font-semibold text-right">₹{parseFloat(invoice.netAmount || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-slate-600 text-center">{invoice.items?.length || 0}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => generatePDF(invoice)}
                              className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center gap-2 mx-auto"
                            >
                              <FiDownloadCloud size={14} />
                              Download
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                          {reportData.length === 0 ? 'No data available. Please run the report.' : 'No invoices found for the selected supplier.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 flex-shrink-0">
                  <div className="text-sm text-slate-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} invoices
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default withAuth(PurchaseReturnReport);
